import { EMPTY_ARRAY } from '@e-socna/empty';

const SETTING_LABELS_FR: Record<string, string> = {
  allowsFileDownload: 'Autorise le téléchargement de fichiers',
  allowsFileUpload: "Autorise l'envoi de fichiers",
  areMissionFieldsRelatedToQuotationEditable:
    'Champs mission liés au devis modifiables',
  areOptionGroupsSelectionEditable:
    'Options du devis modifiables dans le chiffrage',
  askOptionGroupsSelection: 'Demande les options retenues par le client',
  atLeastOneExternal: 'Au moins un acteur externe',
  atLeastOneInternal: 'Au moins un acteur interne',
  autoCreateDocumentIfNotExists: "Crée le document s'il n'existe pas encore",
  autoPrint: 'Impression automatique',
  checkQuotationValidity: 'Vérifie la validité du devis',
  codePrefix: 'Préfixe de code',
  freezeMissionInfo: 'Fige les informations de la mission',
  giveBaseRoleToAllInternalUsers: 'Rôle Base à tous les internes',
  giveBaseRoleToAllRelatedExternalOrganizationMembers:
    'Rôle Base aux membres des organisations externes acteurs',
  giveBaseRoleToAllRelatedInternalOrganizationMembers:
    'Rôle Base aux membres des organisations internes acteurs',
  freezeQuotationInfo: 'Fige les informations du devis',
  generatePennylaneQuote: 'Génère un devis Pennylane',
  isRelatedDocumentEditable: 'Document lié modifiable',
  isTechnical: 'Workflow technique',
  needsCompleteArchiveInfo: "Nécessite les informations d'archive complètes",
  oneAndOnlyOneQuotationEmitter: 'Un seul émetteur du devis',
  oneAndOnlyOneQuotationReceiver: 'Un seul destinataire du devis',
  oneAndOnlyOneEdmDomain: 'Un seul domaine GED',
  refreshFileDirectoryGates: "Rafraîchit les accès à l'espace fichiers",
  serviceUsagesSource: 'Source des usages de services',
  showServiceUsageAggregationDialog:
    "Affiche le choix d'agrégation des prestations",
  useAsDummyForServicesInfo: 'État fictif pour les services',
  useAsDummyForToolkitsInfo: 'État fictif pour les outils',
};

const humanizeSettingKey = (key: string): string =>
  key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .replace(/^./, (char) => char.toUpperCase());

export const translateSettingLabel = (key: string): string =>
  SETTING_LABELS_FR[key] ?? humanizeSettingKey(key);

const SETTING_VALUE_LABELS_FR: Record<string, Record<string, string>> = {
  serviceUsagesSource: {
    ServicesInfo: 'Informations de services',
    QuotationInfo: 'Informations du devis',
  },
};

const translateSettingValue = (key: string, value: string): string =>
  SETTING_VALUE_LABELS_FR[key]?.[value] ?? value;

const stringifyPrimitive = (value: unknown): string => {
  if (typeof value === 'string') {
    return value;
  }

  if (
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    typeof value === 'bigint'
  ) {
    return String(value);
  }

  return '';
};

const NON_SETTING_KEYS = new Set(['_id', '__typename']);

// Flags of a workflow shown with its settings.
const WORKFLOW_FLAG_KEYS = ['isTechnical'];

interface BooleanSettingEntry {
  readonly key: string;
  readonly label: string;
  readonly kind: 'boolean';
  readonly value: boolean;
}

interface ValueSettingEntry {
  readonly key: string;
  readonly label: string;
  readonly kind: 'value';
  readonly value: string;
}

export type SettingEntry = BooleanSettingEntry | ValueSettingEntry;

const extractEntries = (
  settings: Readonly<Record<string, unknown>>,
): readonly SettingEntry[] =>
  Object.entries(settings)
    .filter(([key]) => !NON_SETTING_KEYS.has(key))
    .flatMap((entry): readonly SettingEntry[] => {
      const [key, value] = entry;
      const label = translateSettingLabel(key);

      if (typeof value === 'boolean' || value === null) {
        return [{ key, label, kind: 'boolean', value: value === true }];
      }

      if (typeof value === 'object') {
        return extractEntries(value as Record<string, unknown>);
      }

      return [
        {
          key,
          label,
          kind: 'value',
          value: translateSettingValue(key, stringifyPrimitive(value)),
        },
      ];
    });

export const extractSettings = (raw: unknown): readonly SettingEntry[] => {
  if (typeof raw !== 'object' || raw === null) {
    return EMPTY_ARRAY;
  }

  const { settings, ...fields } = raw as Record<string, unknown>;
  const flags = Object.fromEntries(
    WORKFLOW_FLAG_KEYS.filter((key) => key in fields).map((key) => [
      key,
      fields[key],
    ]),
  );

  return [
    ...extractEntries(flags),
    ...(typeof settings === 'object' && settings !== null
      ? extractEntries(settings as Record<string, unknown>)
      : EMPTY_ARRAY),
  ];
};
